import React, { useEffect, useRef, useState } from 'react';
import { X, Save, Tag } from 'lucide-react';
import alertService from '../../services/alertService';
import productService from '../../services/productService';
import { ProductPriceRequestDTO, ProductPriceResponseDTO } from '../../types/product';
import { SearchableSelect } from '../ui/SearchableSelect';
import { formatFromRaw, getNextRawOnKey, rawFromNumber, parseRawToNumber } from '../../utils/decimalMask';

interface PriceModalProps {
  price?: ProductPriceResponseDTO | null;
  onClose: () => void;
  onSave?: (price: ProductPriceResponseDTO) => void;
  defaultProdutoId?: string;
  lockProdutoId?: boolean;
}

const PriceModal: React.FC<PriceModalProps> = ({ price, onClose, onSave, defaultProdutoId, lockProdutoId }) => {
  const [form, setForm] = useState<ProductPriceRequestDTO>({
    produtoId: '',
    precoCusto: 0,
    precoVenda: 0,
    margemLucro: undefined,
    dataInicio: '',
    dataFim: '',
    observacoes: '',
    ativo: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const DECIMALS = 2;

  // Estados de máscara para preços
  const [precoVendaRaw, setPrecoVendaRaw] = useState<string>(rawFromNumber(0, DECIMALS));
  const [precoCustoRaw, setPrecoCustoRaw] = useState<string>(rawFromNumber(0, DECIMALS));

  const [options, setOptions] = useState<Array<{ id: string; nome?: string; descricao?: string }>>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (price) {
      setForm({
        produtoId: price.produtoId,
        precoCusto: price.precoCusto,
        precoVenda: price.precoVenda,
        margemLucro: price.margemLucro,
        dataInicio: price.dataInicio || '',
        dataFim: price.dataFim || '',
        observacoes: price.observacoes || '',
        ativo: price.ativo,
      });
      setPrecoVendaRaw(rawFromNumber(Number(price.precoVenda || 0), DECIMALS));
      setPrecoCustoRaw(rawFromNumber(Number(price.precoCusto || 0), DECIMALS));
    } else if (defaultProdutoId) {
      setForm((prev) => ({ ...prev, produtoId: defaultProdutoId }));
    }
  }, [price, defaultProdutoId]);

  const onSearchProducts = (term: string) => {
    // Debounce de 300ms
    if (searchTimeoutRef.current) {
      window.clearTimeout(searchTimeoutRef.current);
    }
    const timeoutId = window.setTimeout(async () => {
      const s = (term || '').trim();
      if (!s) { setOptions([]); return; }
      try {
        setSearchLoading(true);
        const res = await productService.searchByTerm(s, { page: 0, size: 10 });
        const opts = (res.data?.content || []).map((p) => ({ id: p.id, nome: p.nome, descricao: p.codigo }));
        setOptions(opts);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    searchTimeoutRef.current = timeoutId;
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.produtoId) errs.produtoId = 'Produto é obrigatório';
    if (!form.precoCusto || form.precoCusto <= 0) errs.precoCusto = 'Preço de custo deve ser maior que zero';
    if (!form.precoVenda || form.precoVenda <= 0) errs.precoVenda = 'Preço de venda deve ser maior que zero';
    if (!form.dataInicio) errs.dataInicio = 'Data início é obrigatória';
    if (form.dataInicio && form.dataFim && new Date(form.dataFim) < new Date(form.dataInicio)) {
      errs.dataFim = 'Data fim deve ser maior que início';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    const parseDecimal = (v: string) => {
      const norm = v.replace(',', '.');
      const num = parseFloat(norm);
      return isNaN(num) ? 0 : num;
    };
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox'
        ? (e.target as HTMLInputElement).checked
        : name === 'margemLucro'
          ? (value === '' ? undefined : parseDecimal(value))
          : name === 'margemLucro'
            ? (value === '' ? undefined : parseDecimal(value))
            : value,
    }));
    if (errors[name]) {
      setErrors((prev) => {
        const n = { ...prev }; delete n[name]; return n;
      });
    }
  };

  const handleBlurTwoDecimals = (field: 'margemLucro') => {
    setForm((prev) => {
      const value = prev[field];
      if (value === undefined || value === null) return prev;
      const fixed = +Number(value).toFixed(2);
      return { ...prev, [field]: fixed } as ProductPriceRequestDTO;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const payload: ProductPriceRequestDTO = {
        produtoId: form.produtoId,
        precoCusto: form.precoCusto,
        precoVenda: form.precoVenda,
        margemLucro: form.margemLucro,
        dataInicio: form.dataInicio,
        dataFim: form.dataFim || undefined,
        observacoes: form.observacoes || undefined,
        ativo: form.ativo,
      };
      const res = price
        ? await productService.updatePrice(price.id, payload)
        : await productService.createPrice(payload);
      if (res.success && res.data) {
        alertService.success(price ? 'Preço atualizado' : 'Preço criado');
        onSave?.(res.data);
        onClose();
      } else {
        alertService.error('Falha ao salvar preço');
      }
    } catch (err: any) {
      alertService.error(err?.response?.data?.message || 'Erro ao salvar preço');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
      <div className="bg-white h-full flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 bg-white flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <div className="flex items-center gap-2 sm:gap-3">
            <Tag className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
              {price ? 'Editar Preço' : 'Novo Preço'}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1">
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              <div>
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">Dados do Preço</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  <div className="md:col-span-2">
                    <SearchableSelect
                      options={options}
                      value={form.produtoId}
                      onChange={(v) => setForm((p) => ({ ...p, produtoId: String(v) }))}
                      label="Produto *"
                      placeholder="Buscar produto"
                      error={errors.produtoId}
                      onSearch={onSearchProducts}
                      loading={searchLoading}
                      disabled={!!price || !!lockProdutoId}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Preço de Venda *</label>
                    <input
                      type="text"
                      name="precoVenda"
                      inputMode="numeric"
                      value={formatFromRaw(precoVendaRaw ?? rawFromNumber(Number(form.precoVenda || 0), DECIMALS), DECIMALS)}
                      onKeyDown={(e) => {
                        const currentRaw = precoVendaRaw ?? rawFromNumber(Number(form.precoVenda || 0), DECIMALS);
                        const { handled, nextRaw } = getNextRawOnKey(e.key, currentRaw);
                        if (!handled) return;
                        e.preventDefault();
                        setPrecoVendaRaw(nextRaw);
                        const parsed = parseRawToNumber(nextRaw, DECIMALS);
                        setForm((prev) => ({ ...prev, precoVenda: parsed }));
                      }}
                      onChange={(e) => {
                        const val = e.target.value || '';
                        const numeric = parseFloat(val.replace(',', '.')) || 0;
                        setPrecoVendaRaw(rawFromNumber(numeric, DECIMALS));
                        setForm((prev) => ({ ...prev, precoVenda: numeric }));
                      }}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-red-50 ${errors.precoVenda ? 'border-red-400' : 'border-red-300'}`}
                      placeholder={formatFromRaw(rawFromNumber(0, DECIMALS), DECIMALS)}
                    />
                    {errors.precoVenda && (<p className="mt-1 text-sm text-red-600">{errors.precoVenda}</p>)}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Preço de Custo *</label>
                    <input
                      type="text"
                      name="precoCusto"
                      inputMode="numeric"
                      value={formatFromRaw(precoCustoRaw ?? rawFromNumber(Number(form.precoCusto || 0), DECIMALS), DECIMALS)}
                      onKeyDown={(e) => {
                        const currentRaw = precoCustoRaw ?? rawFromNumber(Number(form.precoCusto || 0), DECIMALS);
                        const { handled, nextRaw } = getNextRawOnKey(e.key, currentRaw);
                        if (!handled) return;
                        e.preventDefault();
                        setPrecoCustoRaw(nextRaw);
                        const parsed = parseRawToNumber(nextRaw, DECIMALS);
                        setForm((prev) => ({ ...prev, precoCusto: parsed }));
                      }}
                      onChange={(e) => {
                        const val = e.target.value || '';
                        const numeric = parseFloat(val.replace(',', '.')) || 0;
                        setPrecoCustoRaw(rawFromNumber(numeric, DECIMALS));
                        setForm((prev) => ({ ...prev, precoCusto: numeric }));
                      }}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-red-50 ${errors.precoCusto ? 'border-red-400' : 'border-red-300'}`}
                      placeholder={formatFromRaw(rawFromNumber(0, DECIMALS), DECIMALS)}
                    />
                    {errors.precoCusto && (<p className="mt-1 text-sm text-red-600">{errors.precoCusto}</p>)}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data Início *</label>
                    <input
                      type="date"
                      name="dataInicio"
                      value={form.dataInicio || ''}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${errors.dataInicio ? 'border-red-300' : 'border-gray-300'}`}
                    />
                    {errors.dataInicio && (<p className="mt-1 text-sm text-red-600">{errors.dataInicio}</p>)}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data Fim</label>
                    <input
                      type="date"
                      name="dataFim"
                      value={form.dataFim || ''}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${errors.dataFim ? 'border-red-300' : 'border-gray-300'}`}
                    />
                    {errors.dataFim && (<p className="mt-1 text-sm text-red-600">{errors.dataFim}</p>)}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Margem de Lucro (opcional)</label>
                    <input
                      type="number"
                      name="margemLucro"
                      value={form.margemLucro != null ? Number(form.margemLucro).toFixed(2) : ''}
                      onChange={handleChange}
                      onBlur={() => handleBlurTwoDecimals('margemLucro')}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent border-gray-300 bg-white"
                      placeholder="Ex.: 10,00"
                      step="0.01"
                      min="0"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                    <textarea
                      name="observacoes"
                      value={form.observacoes || ''}
                      onChange={(e) => handleChange(e as any)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent border-gray-300"
                      rows={3}
                      maxLength={500}
                      placeholder="Até 500 caracteres"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ativo</label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="ativo"
                        checked={!!form.ativo}
                        onChange={handleChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 bg-gray-50 p-4 sm:p-6 border-t border-gray-200 flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100">
              Cancelar
            </button>
            <button type="submit" disabled={loading} className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 disabled:opacity-70">
              <Save className="h-4 w-4" />
              {loading ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PriceModal;
